import { useTranslation } from 'react-i18next';
import { Modal, Button, InputText, Label } from '@teo/components';
import { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Portal } from '@/components/Portal';
import { axios } from '@/axios';
import { config } from '@/config';
import { useGet } from '@/query/fetchHooks';
import { Tag, ArrowLeft } from '@teo/components/icons';
import { PaginationMultiselectWithTags } from '@/components/PaginationMultiselectWithTags';
import i18n from 'i18next';
import { newUid, getTagsFromImageUrl } from '@/util';
import UploadDropzoneImage from '@/components/UploadDropzoneImage/UploadDropzoneImage';
import { AITags } from './LibraryModal/AITag';

export const NewImageToLibrary = ({
  isOpenNewLibraryModal,
  setIsOpenNewLibraryModal,
  setRefresh = null,
  setIsOpenLibraryModal,
  isOpenLibraryModal,
  onSelectImage,
  isModal = false,
}) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;
  const organisationId = authUser?.activeOrganisationId;
  const [selectTags, setSelectTags] = useState([]);
  const [filter, setFilter] = useState('');
  const [img, setImg] = useState(null);
  const [imgName, setImgName] = useState(null);

  const [aiTags, setAiTags] = useState([]);
  const [aiTagsLoading, setAiTagsLoading] = useState(false);
  const [isloadedAITags, setIsloadedAITags] = useState(false);
  const [name, setName] = useState('');

  const aiRequestedRef = useRef(false);

  const closeModal = () => {
    isModal
      ? setIsOpenLibraryModal(!isOpenLibraryModal)
      : setIsOpenNewLibraryModal(!isOpenNewLibraryModal);
  };

  const handleAddTag = () => {
    if (filter) {
      const promises = [];
      promises.push(
        axios.post(`${config.backend}/tags`, {
          tag: filter,
          label: filter,
          key: Math.floor(Math.random() * 9999999999999),
          organisationId: auth?.user?.activeOrganisationId,
          lang: auth?.user?.lang,
        })
      );
      Promise.allSettled(promises).then((results) => {
        setSelectTags((prev) => [
          ...prev,
          {
            key: filter,
            label: filter,
            tag: filter,
            id: results[0].value?.data?.id,
          },
        ]);
        setFilter('');
      });
    }
  };

  useEffect(() => {
    if (img) {
      aiRequestedRef.current = false;
      const fileName = img.split('/').pop();
      setImgName(fileName.substring(0, fileName.lastIndexOf('.')));
      fetchAITags([]);
    }
  }, [img, isloadedAITags]);

  const fetchAITags = async (existingTags = []) => {
    if (!img || aiRequestedRef.current) return;
    if (!isloadedAITags) return;
    aiRequestedRef.current = true;

    setAiTagsLoading(true);

    const currentExistingTags = existingTags
      .map((el) => el?.label || el?.tag || el)
      .filter(Boolean);

    const authId = auth?.user?.id;
    const result = await getTagsFromImageUrl(img, currentExistingTags, authId);

    setAiTags(result);
    setAiTagsLoading(false);
  };

  const handleAddImage = async () => {
    if (!img || !organisationId) return;

    const arr = selectTags?.map((tag) => tag.tag) || [];
    const imgTags = (arr || []).map((item) => `tag:${item}`).join(';;');

    const newLibraryImage = await axios.post(
      `${config.backend}/imagelibraries`,
      {
        imageUrl: img,
        imageName: name || imgName,
        imageUid: imgName,
        imageType: img.split('.').pop(),
        organisationId,
        tags: imgTags,
      }
    );

    const imageId = newLibraryImage?.data?.id;

    if (imageId && selectTags?.length) {
      for (const tag of selectTags) {
        await axios.post(`${config.backend}/imagetags`, {
          imageId: imageId,
          tag: 'tag:' + tag?.label,
          id: tag?.id,
          organisationId,
        });
      }
    }

    setRefresh && setRefresh(newUid(20));
    onSelectImage && isModal && onSelectImage(img);
    isModal
      ? setIsOpenLibraryModal(!isOpenLibraryModal)
      : setIsOpenNewLibraryModal(false);
  };

  return (
    <Portal root="body">
      <div className="z-10 bg-white">
        <Modal isOpen={isOpenNewLibraryModal}>
          <Modal.Header onClose={() => closeModal()}>
            <div className="flex items-center gap-1">
              {isModal && (
                <Button
                  className="mr-4 !px-2 py-2 text-grey-06"
                  onClick={() => {
                    setIsOpenNewLibraryModal(!isOpenNewLibraryModal);
                  }}
                  variant="ghost"
                  size="sm"
                >
                  <ArrowLeft className="h-5 w-5 text-grey-06" />
                </Button>
              )}
              {t('pages.create_library.add_image')}
            </div>
          </Modal.Header>
          <Modal.Body>
            {!img ? (
              <UploadDropzoneImage
                setUploadImg={(url) => {
                  setImg(url);
                }}
              />
            ) : (
              <div className=" -ml-6 -mr-6 max-h-[50vh]  overflow-y-scroll px-6">
                <div className="flex flex-col sm:flex-row">
                  <div className="flex flex-col pr-2 sm:basis-1/2">
                    <img
                      src={img}
                      alt="image"
                      className="max-h-[165px] w-full max-w-[265px] rounded-lg border border-grey-01 object-cover sm:max-h-[365px] sm:max-w-[365px]"
                    />
                    <div className="mt-4 flex flex-col">
                      <Label htmlFor="imgName">
                        {t('pages.manage_competencies.tab_categories.name')}
                      </Label>
                      <InputText
                        id="imgName"
                        value={
                          name ||
                          img
                            .split('/')
                            .pop()
                            .substring(0, img.split('/').pop().lastIndexOf('.'))
                        }
                        onChange={(ev) => setName(ev.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 sm:basis-1/2 sm:pl-2">
                    <h4 className="text-base">{t('links.tags')}</h4>

                    <p className="text-sm text-grey-06">
                      {t('add_img_to_library.add_tags_m')}
                    </p>
                    <div className="relative z-[10] flex items-center">
                      <Tag className="mr-2 h-[17.5px] w-[17.5px] text-[#6d6d72]" />

                      <div
                        className="w-full"
                        onSelect={() =>
                          !isloadedAITags && setIsloadedAITags(true)
                        }
                      >
                        <PaginationMultiselectWithTags
                          value={selectTags}
                          onChange={(value) => {
                            setSelectTags(value);
                          }}
                          filter={filter}
                          setFilter={setFilter}
                          keyFn={(row) => row?.tag}
                          fetch={useGet.bind(
                            null,
                            `/tags?lang=${i18n.language}&organisationId=${organisationId},OR,organisationId=null`
                          )}
                          defaultSort={[{ id: 'tag', desc: false }]}
                          render={(row) => {
                            return row?.tag;
                          }}
                          customStyles="bg-white"
                          addTagLabel={`${filter} ${t(
                            'pages.create_edit_course.addTagLabel'
                          )}`}
                          handleAddTag={handleAddTag}
                          placeholder={
                            selectTags?.length > 0
                              ? ''
                              : t('pages.create_edit_course.tags_placeholder')
                          }
                        />
                      </div>
                    </div>
                    {isloadedAITags && (
                      <AITags
                        tags={aiTags}
                        loading={aiTagsLoading}
                        setTags={setAiTags}
                        setSelectTags={setSelectTags}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="relative -ml-6 -mr-6 mt-6 flex flex-row items-center gap-3 border-t px-6 pt-6">
              <p className="text-sm text-grey-06">
                {img
                  ? t('add_img_to_library.add_least_tag')
                  : t('pages.create_library.step_choose_image')}
              </p>
              <Button
                className="ml-auto"
                type="button"
                variant="outline"
                onClick={() => {
                  closeModal();
                }}
              >
                {t('widgets.upload_buttons.cancel_button')}
              </Button>

              <Button
                disabled={!img}
                type="button"
                onClick={() => {
                  handleAddImage();
                }}
              >
                {isModal
                  ? t('add_img_to_library.save_to_library_use')
                  : t('add_img_to_library.save_to_library')}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </Portal>
  );
};
